import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ExecutionSalesOrderButtonComponent } from '../../order/sales-order/execution-sales-order-button.component';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { ContainerBookingWorkspace, ContainerBookingWorkspaceReferenceType, ContainerTrackingService } from '../container-tracking.service';
import { ContainerCase } from '../models/container-case.model';

@Component({
  selector: 'app-container-booking-workspace',
  standalone: true,
  imports: [
    CommonModule,
    BreadcrumbModule,
    ButtonModule,
    ProgressBarModule,
    TableModule,
    TagModule,
    TooltipModule,
    ExecutionSalesOrderButtonComponent,
    PageDataPanelComponent,
    PageDetailHeroComponent,
    PageMetricStripComponent,
    PageShellComponent
  ],
  templateUrl: './container-booking-workspace.component.html',
  styleUrls: ['./container-booking-workspace.component.css']
})
export class ContainerBookingWorkspaceComponent implements OnInit {
  isLoading = false;
  loadError = '';
  workspace?: ContainerBookingWorkspace;
  referenceType: ContainerBookingWorkspaceReferenceType = 'BOOKING';
  referenceNumber = '';
  breadcrumbItems: MenuItem[] = [
    { label: 'Container Tracking', routerLink: '/container-tracking/cases' },
    { label: 'Cases', routerLink: '/container-tracking/cases' },
    { label: 'Booking Workspace' }
  ];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private containerTrackingService: ContainerTrackingService,
    private sessionStorageService: SessionStorageService
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      const referenceType = params.get('referenceType');
      this.referenceType = referenceType === 'BILL_OF_LADING' ? 'BILL_OF_LADING' : 'BOOKING';
      this.referenceNumber = (params.get('referenceNumber') || '').trim();
      this.loadWorkspace();
    });
  }

  get title(): string {
    const noun = this.referenceType === 'BOOKING' ? 'Booking' : 'Bill of Lading';
    return `${noun} ${this.referenceNumber ? '# ' + this.referenceNumber : 'Workspace'}`;
  }

  get subtitle(): string {
    const count = this.workspace?.containerCases?.length || 0;
    return `Commercial and lifecycle context for ${count} container ${count === 1 ? 'case' : 'cases'}. Sales orders are created once from the shared booking project.`;
  }

  get metrics(): PageMetric[] {
    const cases = this.workspace?.containerCases || [];
    return [
      { label: 'Container Cases', value: cases.length, tone: 'info' },
      { label: 'In Progress', value: cases.filter(item => (item.status || '').toUpperCase() === 'IN_PROGRESS').length, tone: 'warn' },
      { label: 'On Hold', value: cases.filter(item => (item.status || '').toUpperCase() === 'ON_HOLD').length, tone: 'danger' },
      { label: 'Active Transport', value: cases.filter(item => !!item.activeTransportLegId).length, tone: 'info' },
      { label: 'Active Storage', value: cases.filter(item => !!item.activeStorageLegId).length, tone: 'warn' }
    ];
  }

  loadWorkspace(): void {
    const owningCompanyId = this.sessionStorageService.getCompany()?.id;
    if (!owningCompanyId || !this.referenceNumber) {
      this.loadError = 'Choose a booking or bill of lading from Container Cases to open its workspace.';
      return;
    }
    this.isLoading = true;
    this.loadError = '';
    this.containerTrackingService.findBookingWorkspace(owningCompanyId, this.referenceType, this.referenceNumber)
      .subscribe({
        next: workspace => {
          this.workspace = workspace;
          this.isLoading = false;
          this.breadcrumbItems = [
            { label: 'Container Tracking', routerLink: '/container-tracking/cases' },
            { label: 'Cases', routerLink: '/container-tracking/cases' },
            { label: this.referenceType === 'BOOKING' ? `Booking ${this.referenceNumber}` : `BL ${this.referenceNumber}` }
          ];
        },
        error: error => {
          this.workspace = undefined;
          this.isLoading = false;
          this.loadError = error?.error?.message || 'The booking workspace could not be loaded.';
        }
      });
  }

  openProject(): void {
    if (this.workspace?.logisticsProjectId) {
      this.router.navigate(['/logistics-projects', this.workspace.logisticsProjectId]);
    }
  }

  openCase(containerCase: ContainerCase): void {
    if (containerCase.id) {
      this.router.navigate(['/container-tracking/cases/view', containerCase.id]);
    }
  }

  backToCases(): void {
    this.router.navigate(['/container-tracking/cases']);
  }

  statusSeverity(status?: string | null): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch ((status || '').toUpperCase()) {
      case 'COMPLETED': return 'success';
      case 'IN_PROGRESS': return 'warn';
      case 'ON_HOLD': return 'danger';
      case 'OPEN': return 'info';
      default: return 'secondary';
    }
  }

  controlLabel(containerCase: ContainerCase): string {
    if (containerCase.activeTransportLegId) return 'Transport active';
    if (containerCase.activeStorageLegId) return 'Storage active';
    return 'No active control';
  }

  hasJourneyProgress(containerCase: ContainerCase): boolean {
    return (containerCase.journeyStepCount || 0) > 0;
  }

  journeyProgressPercent(containerCase: ContainerCase): number {
    const value = Number(containerCase.journeyProgressPercent);
    if (!Number.isFinite(value)) {
      return 0;
    }
    return Math.min(100, Math.max(0, Math.round(value)));
  }

  journeyProgressCaption(containerCase: ContainerCase): string {
    const total = containerCase.journeyStepCount || 0;
    const completed = containerCase.journeyCompletedStepCount || 0;
    const progress = `${completed} of ${total} required step${total === 1 ? '' : 's'} completed`;
    return containerCase.status === 'ON_HOLD' ? `${progress} | On hold` : progress;
  }

  journeyCurrentStepLabel(containerCase: ContainerCase): string {
    return containerCase.journeyCurrentStepName
      || containerCase.currentState
      || 'Awaiting journey step';
  }
}
